import dayjs from "dayjs"
import { set } from "lodash-es"
import { action, makeAutoObservable, observable } from "mobx"

import APIReturnPage from "@/api/returnpage"
import { ICommonReturn, IReturnData } from "@/api/returnpage/type"
import {
  IReturnStyleConfig,
  IReturnTextConfig,
  LoginType,
} from "@/api/returnpage/type/returnPageConfig"
import { RTPageModuleType } from "@/pages/ReturnPage/type"
import PopupsUtils from "@/utils/PopupsUtils"
import toast from "@/utils/toast"

import { langInfo } from "../Language"
import DefaultPopupsConfig from "./DefaultPopupsConfig"

// 通过id值聚焦input
const focusInputById = (id: string, isDelay?: boolean, delay = 20) => {
  function focusInput() {
    const targetElement = document.getElementById(id) as HTMLInputElement

    if (targetElement) {
      targetElement?.focus()
      targetElement.selectionStart = targetElement.value.length // 设置光标位置为内容的末尾
      targetElement.selectionEnd = targetElement.value.length // 设置选中范围的结束位置为内容的末尾
    }
  }
  if (isDelay) {
    setTimeout(() => {
      focusInput()
    }, delay)
  } else {
    focusInput()
  }
}

export default class ReturnPageStore implements ICommonReturn {
  // @observable tabSelected: PopupsTab = PopupsTab.StartStatus
  @observable dataLoadedTime: number = 0
  @observable id: number = 0
  @observable uid: number = 0
  @observable name: string = "Default Login Form"
  @observable isPolicy: boolean = false
  @observable status: boolean = false
  @observable text: IReturnTextConfig = DefaultPopupsConfig.text
  @observable style: IReturnStyleConfig = DefaultPopupsConfig.style
  @observable is_remove_brand = DefaultPopupsConfig.is_remove_brand
  @observable store_contact_info = ""
  @observable fontList = []
  @observable languageList: langInfo[] = []
  @observable activeLang: string = ""
  @observable displayName: string = ""
  // 切换语言时的loading效果
  @observable loading: boolean = false
  // 编辑器左侧切换栏
  @observable leftSelected: number = 0
  // 页面当前激活编辑区域
  @observable public activeModule?: RTPageModuleType
  // 页面当前tabs的激活项
  @observable public activeTabsIndex: number = 0
  // 页面当前logo的激活项
  @observable public activeLogoIndex: number = 0
  // return page小部件的顶部导航条
  @observable addWidgetLoading = {
    saveLoading: false,
    saveBtnActive: false,
  }
  // return page小部件 targetLink
  @observable widgetTargetLink: string = ""

  constructor() {
    // 使用这个才会在 MobX 6 上才会更新视图
    makeAutoObservable(this)
  }

  /**
   * 切换当前编辑区域
   */
  @action.bound
  public setActiveModule(type?: RTPageModuleType, toFocus = false): void {
    // 如果为当前，则返回不执行后面逻辑
    if (!type && !toFocus) {
      this.activeLogoIndex = 0
    }
    if (type === this.activeModule) return

    this.activeModule = type

    switch (type) {
      case RTPageModuleType.title:
        this.leftSelected = 2
        focusInputById("rtp-title-container", true)
        this.activeTabsIndex = 0
        this.activeLogoIndex = 0
        break
      case RTPageModuleType.position1:
        this.leftSelected = 1
        this.activeTabsIndex = 0
        this.activeLogoIndex = 0
        focusInputById("rtp-body_position1", true)
        break
      case RTPageModuleType.position2:
        this.leftSelected = 1
        this.activeTabsIndex = 0
        this.activeLogoIndex = 0
        focusInputById("rtp-body_position2", true)
        break
      case RTPageModuleType.position3:
        this.leftSelected = 1
        this.activeTabsIndex = 0
        focusInputById("rtp-body_position3", true)
        break
      case RTPageModuleType.position4:
        this.leftSelected = 1
        this.activeTabsIndex = 0
        this.activeLogoIndex = 0
        focusInputById("rtp-body_position4", true)
        break
      case RTPageModuleType.buttonName:
        this.leftSelected = 1
        this.activeTabsIndex = 0
        this.activeLogoIndex = 0
        focusInputById("rtp-button_position", true)
        break
      case RTPageModuleType.logo:
        this.leftSelected = 2
        this.activeTabsIndex = 0
        this.activeLogoIndex = 1
        break
      case RTPageModuleType.exchangeLogo:
        this.leftSelected = 2
        this.activeTabsIndex = 1
        this.activeLogoIndex = 2
        break
      case RTPageModuleType.exchangeTitle:
        this.leftSelected = 2
        this.activeTabsIndex = 1
        this.activeLogoIndex = 0
        focusInputById("rtp-exchange-title", true)
        break
    }
  }

  /**
   * 删除logo图标
   */
  @action.bound
  public setActiveLogoIndex(val: number) {
    this.activeLogoIndex = val
  }

  /**
   * 设置左侧栏切换
   */
  @action.bound
  public setLeftSelected(select: number) {
    this.leftSelected = select
  }

  /**
   * 设置tabs栏切换
   */
  @action.bound
  public setActiveTabsIndex(val: number) {
    this.activeTabsIndex = val
  }

  /**
   * 设置语言列表
   */
  @action.bound
  public setLanguageList(list: langInfo[]) {
    const default_lang = list.find((langInfo) => langInfo.is_default === 1) as {
      code: string
      name: string
      ename: string
    }

    this.activeLang = default_lang.code
    this.displayName = default_lang.ename
    this.languageList = list
  }

  /**
   * 切换状态
   */
  @action.bound
  setActiveStatus(code: string): void {
    this.activeLang = code
    const select_item = this.languageList.find(
      (langInfo) => langInfo.code === code,
    ) as { name: string; ename: string }

    this.displayName = select_item.ename
    this.loading = true
    APIReturnPage.getReturnConfig({ code })
      .then((res) => {
        const {
          data: { code, data },
        } = res
        if (code !== 1000) {
          toast("Request error", 4e3, true)
          return
        }

        if (code === 1000) {
          this.setConfig(data)
        }
      })
      .finally(() => {
        this.loading = false
      })
  }

  @action.bound
  setFontList(data) {
    this.fontList = data
  }

  @action.bound
  setIsRemoveBrand(val) {
    this.is_remove_brand = Number(val)
  }

  @action.bound
  setStoreContactInfo(val: string) {
    this.store_contact_info = val
  }

  @action.bound
  setIsPolicy(value: boolean) {
    this.isPolicy = value
  }

  @action.bound
  setTextConfig<T>(path: string, v: T) {
    set(this.text, path, v)
  }

  @action.bound
  setStyleConfig<T>(path: string, v: T) {
    set(this.style, path, v)
  }

  @action.bound
  setConfig(data: IReturnData) {
    PopupsUtils.setInitOptions(data)
    this.dataLoadedTime = dayjs().unix()
    this.id = data.id
    this.text = data.text
    this.style = data.style
    this.is_remove_brand = data.is_remove_brand
    this.store_contact_info = data.store_contact_info || ""
  }

  @action.bound
  setDataLoadedTime(time: number) {
    this.dataLoadedTime = time
  }

  @action.bound
  setAddWidgetLoading(path: string, val: boolean) {
    this.addWidgetLoading[path] = val
  }
  @action.bound
  setWidgetTargetLink(val: string) {
    this.widgetTargetLink = val
  }

  @action.bound
  getCustomText() {
    return {
      email_address:
        this.text.order_lookup_method === LoginType.Email_With_Code
          ? this.text.email_address
          : this.text.order_order_number,
      verification_code:
        this.text.order_lookup_method === LoginType.Email_With_Code
          ? this.text.verification_code
          : this.text.order_email_address,
      bottom_description: this.text.bottom_description,
      return_policy_link: this.text.return_policy_link.value,
      confirm_button_name: this.text.confirm_button_name,
      email_address_error_tips: this.text.email_address_error_tips,
      verification_code_error_tips: this.text.verification_code_error_tips,
      policy_content: this.text.return_policy_link.content,
      order_lookup_method: this.text.order_lookup_method,
      return_policy_link_text_name: this.text.return_policy_link_text_name,
      email_or_phone_error_tips: this.text.email_or_phone_error_tips,
    }
  }

  @action.bound
  getCustomStyle() {
    return {
      background_image: this.style.background_image,
      logo: this.style.portal_top_content.url,
      header_title: this.style.portal_top_content.value,
      type: this.style.portal_top_content.type,
      theme_color: this.style.return_page_primary_color,
      corner_radius: this.style.login_pop_up.corner_radius,
      opacity: this.style.login_pop_up.opacity,
      width: this.style.portal_top_logo_width,
      btn_text_color: this.style.primary_btn_text_color,
    }
  }
}
